import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
    Users,
    HeartPulse,
    Home,
    MapPin as MapPinIcon,
    ShieldCheck,
    Lock,
    Sparkles,
    ArrowRight,
    CheckCircle2,
    Cloud,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { SITE, IMAGES } from "../constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
    {
        icon: Users,
        title: "Family Hub",
        subtitle: "Your family's information, safely connected.",
        body: "Store and manage family member profiles, important documents, emergency contacts, medical information, and shared access — all protected with enterprise-grade security and under your control.",
        accent: "from-[#1fb6c4]/15 to-[#1fb6c4]/0",
        iconColor: "text-brand-teal",
        span: "md:col-span-2",
    },
    {
        icon: HeartPulse,
        title: "Health Hub",
        subtitle: "Your medical records, when you need them most.",
        body: "Securely store prescriptions, medical history, allergies, healthcare providers, and health documents. You choose who can access your information, and only with your permission.",
        accent: "from-[#7351b7]/15 to-[#7351b7]/0",
        iconColor: "text-brand-purple",
        span: "md:col-span-1",
    },
    {
        icon: Home,
        title: "Home Hub",
        subtitle: "Everything about your home in one secure place.",
        body: "Keep records of household assets, warranties, insurance documents, service providers, maintenance history, and trusted household staff, all organised and easily accessible.",
        accent: "from-[#4a7bd9]/15 to-[#4a7bd9]/0",
        iconColor: "text-brand-blue",
        span: "md:col-span-1",
    },
    {
        icon: MapPinIcon,
        title: "Safety Hub",
        subtitle: "Keeping your loved ones connected and protected.",
        body: "Share your live location with trusted family members, access emergency information from your lock screen, and quickly provide critical details during emergencies — while maintaining full control over your privacy.",
        accent: "from-[#f47b60]/15 to-[#f47b60]/0",
        iconColor: "text-brand-coral",
        span: "md:col-span-2",
    },
];

export default function Landing() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [joined, setJoined] = useState(false);

    const handleWaitlist = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/waitlist`, { email });
            setJoined(true);
            setEmail("");
            toast.success("You're on the list. Welcome to Kinora OS.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="landing-page" className="min-h-screen bg-background">
            <Navbar />

            {/* HERO */}
            <section className="relative pt-32 lg:pt-40 pb-24 lg:pb-32 overflow-hidden">
                <div className="absolute inset-0 -z-10 grain opacity-70" />
                <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full brand-gradient-soft blur-3xl -z-10" />
                <div className="absolute -bottom-40 -left-40 h-[32rem] w-[32rem] rounded-full brand-gradient-soft blur-3xl -z-10" />

                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            className="lg:col-span-7"
                        >
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-medium text-slate-700 mb-8">
                                <Sparkles className="h-3.5 w-3.5 text-brand-purple" />
                                Built for busy South African families
                            </div>

                            <h1 className="font-display font-bold text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                Your family's central{" "}
                                <span className="text-brand-gradient">
                                    operating system
                                </span>
                                .
                            </h1>

                            <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl">
                                {SITE.appName} keeps everything your family
                                depends on in one calm, private place — shared
                                calendars, lists, medical records, important
                                documents, and a secure photo vault. Less
                                juggling. More living.
                            </p>

                            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <Button
                                    asChild
                                    size="lg"
                                    data-testid="hero-primary-cta"
                                    className="rounded-full brand-gradient text-white border-0 px-7 h-12 shadow-lg shadow-brand-purple/20 hover:-translate-y-0.5 transition-all"
                                >
                                    <a href="#waitlist">
                                        Get early access
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </a>
                                </Button>
                                <Button
                                    asChild
                                    size="lg"
                                    variant="ghost"
                                    data-testid="hero-secondary-cta"
                                    className="rounded-full px-6 h-12 text-slate-700 hover:bg-slate-100"
                                >
                                    <a href="#features">See what's inside</a>
                                </Button>
                            </div>

                            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
                                <span className="inline-flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-brand-teal" />
                                    POPIA compliant
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-brand-teal" />
                                    End-to-end encrypted vault
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-brand-teal" />
                                    No ads, ever
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="lg:col-span-5 relative"
                        >
                            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200 bg-gradient-to-br from-amber-50 to-rose-50">
                                <img
                                    src={IMAGES.heroFamily}
                                    alt="Illustrated multi-generational family together at home"
                                    className="w-full h-[520px] object-cover"
                                />
                            </div>

                            {/* Floating logo card */}
                            <div className="absolute -bottom-6 -left-6 sm:-left-10 rounded-2xl bg-white/90 backdrop-blur-xl ring-1 ring-slate-200 p-4 shadow-xl w-64">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={SITE.logo}
                                        alt="Kinora OS icon"
                                        className="h-12 w-12 rounded-xl"
                                    />
                                    <div>
                                        <div className="font-display font-semibold text-slate-900">
                                            {SITE.appName}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            One home for family life
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating badge */}
                            <div className="hidden sm:flex absolute -top-4 -right-4 items-center gap-2 rounded-full bg-white ring-1 ring-slate-200 shadow-lg px-3 py-2">
                                <ShieldCheck className="h-4 w-4 text-brand-teal" />
                                <span className="text-xs font-semibold text-slate-800">
                                    POPIA Ready
                                </span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section
                id="features"
                data-testid="features-section"
                className="py-24 bg-white border-t border-slate-100"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-purple">
                            What's inside
                        </div>
                        <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
                            Everything your family
                            <br className="hidden sm:block" /> juggles — in one
                            calm home.
                        </h2>
                        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                            Four core modules, designed to work together.
                            Familiar enough that Ouma can use it, powerful
                            enough that you'll finally trust one app with
                            everything.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {features.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <motion.div
                                    key={f.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{
                                        duration: 0.55,
                                        delay: i * 0.08,
                                    }}
                                    className={f.span}
                                >
                                    <Card
                                        data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className={`relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
                                    >
                                        <div
                                            className={`absolute inset-0 -z-10 bg-gradient-to-br ${f.accent}`}
                                        />
                                        <div className={`inline-flex items-center justify-center rounded-xl bg-white ring-1 ring-slate-200 p-3 shadow-sm ${f.iconColor}`}>
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <h3 className="font-display mt-5 text-xl font-semibold text-slate-900">
                                            {f.title}
                                        </h3>
                                        <p className={`mt-1 text-sm font-medium ${f.iconColor}`}>
                                            {f.subtitle}
                                        </p>
                                        <p className="mt-3 text-slate-600 leading-relaxed">
                                            {f.body}
                                        </p>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Your Privacy Comes First */}
            <section
                id="privacy-first"
                data-testid="privacy-first-section"
                className="py-20 bg-slate-50 border-t border-slate-100"
            >
                <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
                    <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm mb-6">
                        <Lock className="h-6 w-6 text-brand-purple" />
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                        Your Privacy Comes First
                    </h2>
                    <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        At {SITE.appName}, your data belongs to you. We never
                        sell your personal information. Every piece of data you
                        share is encrypted, protected, and only accessible by
                        the people you authorise. You remain in complete control
                        of your information at all times.
                    </p>
                </div>
            </section>

            {/* CTA / Waitlist */}
            <section
                id="waitlist"
                data-testid="waitlist-section"
                className="py-24"
            >
                <div className="max-w-4xl mx-auto px-6 sm:px-8">
                    <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-10 sm:p-14 text-center shadow-lg">
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[36rem] brand-gradient-soft blur-3xl -z-0" />
                        <div className="relative">
                            <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5">
                                <Cloud className="h-3.5 w-3.5" />
                                Join the Kinora OS waitlist
                            </div>
                            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
                                Be first through the door.
                            </h2>
                            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
                                We're opening early access in small waves. Add
                                your email and we'll invite you the moment your
                                family's spot is ready.
                            </p>

                            {joined ? (
                                <div
                                    data-testid="waitlist-success"
                                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-3 font-medium"
                                >
                                    <CheckCircle2 className="h-5 w-5" />
                                    You're on the list. See you soon.
                                </div>
                            ) : (
                                <form
                                    onSubmit={handleWaitlist}
                                    data-testid="waitlist-form"
                                    className="mt-8 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto"
                                >
                                    <Input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        placeholder="you@yourfamily.co.za"
                                        data-testid="waitlist-email-input"
                                        className="h-12 rounded-full px-5 border-slate-300 focus-visible:ring-brand-teal"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="waitlist-submit-button"
                                        className="h-12 rounded-full px-7 brand-gradient text-white border-0 hover:-translate-y-0.5 transition-all disabled:opacity-70"
                                    >
                                        {submitting ? "Joining…" : "Join waitlist"}
                                    </Button>
                                </form>
                            )}
                            <p className="mt-4 text-xs text-slate-500">
                                We'll only email you about early access. No
                                marketing spam. Unsubscribe with one click.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
